import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { AUTH_QUERY_KEY } from '../account/useAuth';
import * as profileApi from './profileApi';
import type { ProfilePayload } from './profileApi';

export function profileQueryKey(username: string): readonly unknown[] {
  return ['profile', username.toLowerCase()] as const;
}

export function useProfile(username: string | undefined): UseQueryResult<ProfilePayload, Error> {
  return useQuery({
    queryKey: profileQueryKey(username ?? ''),
    queryFn: () => profileApi.getPublicProfile(username as string),
    enabled: !!username,
  });
}

export function useClaimUsername(): UseMutationResult<
  { username: string },
  Error,
  { username: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username }) => profileApi.claimUsername(username),
    onSuccess: (data) => {
      // Refresh /auth/me so the AccountChip + everywhere else picks up
      // the new username right away.
      void qc.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
      // Pre-warm the profile query for this username so navigating
      // straight to /u/<username> doesn't refetch.
      void qc.invalidateQueries({
        queryKey: profileQueryKey(data.username),
      });
    },
  });
}
